/**
 * RCC Surface #5 — Adverse Action Manager.
 *
 * Reg B 30-day clock. Compiles a notice via /templates/fill then dispatches
 * via /v3/notifications/dispatch (physical channel — regulatory requirement).
 */

import { useCallback, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { Button, Pill } from '@/components/primitives';
import { SurfaceHeader } from '../components/SurfaceHeader';

type Stage = 'idle' | 'compiling' | 'dispatching' | 'sent' | 'error';

export function AdverseAction(): JSX.Element {
  const apiClient = useApiClient();
  const { user } = useAuth();
  const { config } = useConfig();
  const [applicantName, setApplicantName] = useState<string>('');
  const [applicantAddress, setApplicantAddress] = useState<string>('');
  const [reasonCode, setReasonCode] = useState<string>('');
  const [decisionDate, setDecisionDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [stage, setStage] = useState<Stage>('idle');
  const [error, setError] = useState<string | null>(null);

  const adverseTemplate = (config.notificationTemplates?.templates ?? []).find((t) =>
    t.templateName?.toLowerCase().includes('adverse'),
  );
  const templateName = adverseTemplate?.templateName ?? 'adverse-action-notice';

  const daysRemaining = useCallback((): number => {
    const dec = new Date(decisionDate);
    if (Number.isNaN(dec.getTime())) return 0;
    const deadline = new Date(dec.getTime() + 30 * 24 * 60 * 60 * 1000);
    return Math.ceil((deadline.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  }, [decisionDate]);

  // P1 fix 2026-04-27: enforce role gate (Reg B + ECOA — only authorized
  // officers may issue adverse-action notices). Earlier UI label said
  // "Supervisor approval" but no actual check existed.
  const allowedRoles = new Set(['Officer', 'Supervisor', 'Manager', 'SuperAdmin']);
  const userRoles = (user?.roles ?? []) as ReadonlyArray<string>;
  const isAuthorized = userRoles.some((r) => allowedRoles.has(r));

  const issue = useCallback(async (): Promise<void> => {
    if (!isAuthorized) {
      setError('You are not authorized to issue Adverse Action notices. Required role: Officer / Supervisor / Manager.');
      setStage('error');
      return;
    }
    if (!applicantName.trim() || !applicantAddress.trim() || !reasonCode.trim()) {
      setError('Applicant name, address, and reason code are required.');
      setStage('error');
      return;
    }
    setError(null);
    setStage('compiling');
    try {
      const filled = await apiClient.templates.fill({
        TemplateCode: templateName,
        FieldValues: {
          applicantName,
          applicantAddress,
          reasonCode,
          decisionDate,
          bankName: 'Peoples Community Bank',
        },
        Flatten: true,
      });

      if (!filled.success) throw new Error(filled.error ?? 'Template fill failed');

      setStage('dispatching');
      await apiClient.retail.notifications.dispatch({
        templateName,
        category: 'adverse-action',
        channel: 'physical',
        variables: {
          applicantName,
          applicantAddress,
          reasonCode,
          decisionDate,
          bankName: 'Peoples Community Bank',
        },
        recipient: { name: applicantName, channel: 'physical' },
        bypassOptOut: true,
      });

      void apiClient.retail.events
        .write({
          eventType: 'adverse-action-issued',
          eventCategory: 'compliance',
          severity: 'warn',
          actor: user?.upn ?? 'unknown',
          payloadJson: JSON.stringify({ applicantName, reasonCode, decisionDate }),
        })
        .catch(() => undefined);

      setStage('sent');
    } catch (err) {
      setStage('error');
      setError(err instanceof Error ? err.message : 'Failed to issue adverse action notice');
    }
  }, [adverseTemplate, apiClient, applicantAddress, applicantName, decisionDate, reasonCode, user]);

  const remaining = daysRemaining();

  return (
    <div>
      <SurfaceHeader
        title="Adverse Action Manager"
        scope="Reg B 30-day clock. Notices generated here dispatch via RNC → physical mail (regulatory channel requirement)."
        surfaceNumber={5}
        role="FSR · Supervisor approval"
        actions={
          <Pill variant={remaining < 7 ? 'err' : remaining < 14 ? 'warn' : 'info'}>
            {remaining > 0 ? `${remaining} days remaining` : 'Past deadline'}
          </Pill>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--sp-5)', maxWidth: 640 }}>
        <Field
          label="Applicant name"
          value={applicantName}
          onChange={setApplicantName}
          placeholder="First Last"
        />
        <Field
          label="Applicant address"
          value={applicantAddress}
          onChange={setApplicantAddress}
          placeholder="Street, City, State ZIP"
          textarea
        />
        <Field
          label="Reason code"
          value={reasonCode}
          onChange={setReasonCode}
          placeholder="E.g. AA-02 Insufficient Credit References"
        />
        <Field label="Decision date" value={decisionDate} onChange={setDecisionDate} type="date" />

        {error && (
          <div
            role="alert"
            style={{
              padding: 'var(--sp-5)',
              background: 'var(--err-bg)',
              color: 'var(--err-text)',
              borderRadius: 'var(--r-md)',
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
          <Button
            variant="primary"
            onClick={() => void issue()}
            disabled={stage === 'compiling' || stage === 'dispatching'}
          >
            {stage === 'compiling' ? 'Compiling…' : stage === 'dispatching' ? 'Dispatching…' : 'Issue notice'}
          </Button>
          {stage === 'sent' && <Pill variant="ok">Notice dispatched (physical)</Pill>}
        </div>

        {!adverseTemplate && (
          <div style={{ fontSize: 12, color: 'var(--pcb-amber-text)' }}>
            No adverse-action template found in NotificationTemplates config — using fallback template id.
          </div>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  textarea?: boolean;
}): JSX.Element {
  const id = `aa-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: 11,
          textTransform: 'uppercase',
          color: 'var(--ink-500)',
          marginBottom: 'var(--sp-3)',
        }}
      >
        {label}
      </label>
      {textarea ? (
        <textarea
          id={id}
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: 'var(--sp-4)',
            border: '1px solid var(--ink-300)',
            borderRadius: 'var(--r-md)',
            fontFamily: 'var(--font-sans)',
            fontSize: 13,
          }}
        />
      ) : (
        <input
          id={id}
          type={type ?? 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: 'var(--sp-4)',
            border: '1px solid var(--ink-300)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
          }}
        />
      )}
    </div>
  );
}
